import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import config from '../config';
import FilterBar from '../shared/FilterBar';
import StatTile from '../shared/StatTile';
import DataTable from '../shared/DataTable';
import Pagination, { normalizePagination } from '../shared/Pagination';
import { buildParams } from '../shared/apiParams';

const API_URL = config.apiUrl;

const TABS = [
  { key: 'block', label: 'By Block', col: 'Block Name' },
  { key: 'gp', label: 'By Gram Panchayat', col: 'Gram Panchayat' },
  { key: 'village', label: 'By Village', col: 'Village Name' },
  { key: 'enumerator', label: 'By Enumerator', col: 'Enumerator ID' },
];

const EMPTY = { blocks: [], gramPanchayats: [], villages: [], enumerators: [], startDate: '', endDate: '' };

const EMPTY_STATS = {
  totalSurveys: 0, withReading: 0, missingReading: 0,
  within30: 0, outside30: 0, within30Pct: 0, outside30Pct: 0,
  avgPrecisionM: 0, medianPrecisionM: 0, minPrecisionM: 0, maxPrecisionM: 0,
};

export default function GpsAccuracy() {
  const [stats, setStats] = useState(EMPTY_STATS);
  const [applied, setApplied] = useState(EMPTY);
  const [loadingStats, setLoadingStats] = useState(false);

  const [activeTab, setActiveTab] = useState('block');
  const [rows, setRows] = useState([]);
  const [loadingTable, setLoadingTable] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState(normalizePagination({}));

  const tab = TABS.find((t) => t.key === activeTab);

  const fetchStats = useCallback(async (filters) => {
    setLoadingStats(true);
    try {
      const params = buildParams(filters);
      const res = await axios.get(`${API_URL}/analytics/gps-accuracy?${params}`);
      if (res.data?.success) setStats({ ...EMPTY_STATS, ...res.data.data });
    } catch (err) {
      console.error('Error fetching GPS accuracy stats:', err);
    } finally {
      setLoadingStats(false);
    }
  }, []);

  const fetchBreakdown = useCallback(async () => {
    setLoadingTable(true);
    try {
      const params = buildParams(applied, { extra: { page, limit: pageSize, forceGroupBy: activeTab } });
      const res = await axios.get(`${API_URL}/analytics/gps-accuracy/breakdown?${params}`);
      if (res.data?.success) {
        setRows(res.data.data || []);
        setPagination(normalizePagination(res.data.pagination || {}));
      }
    } catch (err) {
      console.error('Error fetching GPS accuracy breakdown:', err);
    } finally {
      setLoadingTable(false);
    }
  }, [applied, page, pageSize, activeTab]);

  useEffect(() => { fetchStats(applied); }, [applied, fetchStats]);
  useEffect(() => { fetchBreakdown(); }, [fetchBreakdown]);

  const columns = [
    { key: 'name', label: tab.col, render: (r) => r.name || 'Unknown' },
    { key: 'totalSurveys', label: 'Total Surveys', align: 'center' },
    { key: 'within30', label: 'Within 30m', align: 'center', render: (r) => <span className="badge badge-success">{r.within30}</span> },
    { key: 'outside30', label: 'Outside 30m', align: 'center', render: (r) => <span className="badge badge-danger">{r.outside30}</span> },
    { key: 'within30Pct', label: '% Within 30m', align: 'center', render: (r) => `${r.within30Pct}%` },
    { key: 'avgPrecisionM', label: 'Avg Precision (m)', align: 'center', render: (r) => r.avgPrecisionM ?? '—' },
    { key: 'medianPrecisionM', label: 'Median Precision (m)', align: 'center', render: (r) => r.medianPrecisionM ?? '—' },
  ];

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">
          <GpsFixedIcon /> GPS Accuracy
        </h1>
        <p className="page-subtitle">
          Share of surveys with a device-reported GPS accuracy radius of 30m or better, vs. worse than 30m.
        </p>
      </div>

      <FilterBar
        onApply={(v) => { setApplied(v); setPage(1); }}
        onClear={() => { setApplied(EMPTY); setPage(1); }}
        loading={loadingStats || loadingTable}
      />

      <div className="stat-tile-row">
        <StatTile label="Total Surveys" value={stats.totalSurveys} icon={GpsFixedIcon} accent="var(--primary)" />
        <StatTile label="Within 30m" value={stats.within30} icon={GpsFixedIcon} accent="var(--success)" sub={`${stats.within30Pct}% of readings`} />
        <StatTile label="Outside 30m" value={stats.outside30} icon={GpsFixedIcon} accent="var(--danger)" sub={`${stats.outside30Pct}% of readings`} />
        <StatTile label="Median Precision" value={`${stats.medianPrecisionM} m`} icon={GpsFixedIcon} accent="var(--info)" />
      </div>

      <div className="card">
        <h3 className="card-title">Overall Split</h3>
        <div className="proportion-bar">
          {stats.within30Pct > 0 && (
            <div className="proportion-seg proportion-seg-good" style={{ flexBasis: `${stats.within30Pct}%` }}>
              {stats.within30Pct >= 8 ? `${stats.within30Pct}%` : ''}
            </div>
          )}
          {stats.outside30Pct > 0 && (
            <div className="proportion-seg proportion-seg-bad" style={{ flexBasis: `${stats.outside30Pct}%` }}>
              {stats.outside30Pct >= 8 ? `${stats.outside30Pct}%` : ''}
            </div>
          )}
        </div>
        <div className="proportion-legend">
          <span><i className="dot dot-good" /> Within 30m — {stats.within30} surveys ({stats.within30Pct}%)</span>
          <span><i className="dot dot-bad" /> Outside 30m — {stats.outside30} surveys ({stats.outside30Pct}%)</span>
        </div>
      </div>

      <div className="tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            className={`tab-btn${activeTab === t.key ? ' active' : ''}`}
            onClick={() => {
              if (t.key !== activeTab) {
                setActiveTab(t.key);
                setPage(1);
              }
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="card">
        <h3 className="card-title">Breakdown by {tab.label.replace('By ', '')}</h3>
        <Pagination
          pagination={pagination}
          onPageChange={setPage}
          pageSize={pageSize}
          onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
          unitLabel="groups"
          shown={rows.length}
        />
        <DataTable
          columns={columns}
          rows={rows}
          loading={loadingTable}
          emptyMessage='No data found. Adjust filters and click "Apply Filters".'
          getRowKey={(r, i) => `${r.name}-${i}`}
        />
      </div>
    </div>
  );
}
